import styled from 'styled-components';
import { Button } from '@mui/material';
import {tokens} from "../../theme";

export const OcrBatchCard = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
`;

interface MainContainerProps {
    $row?: boolean;
}

export const OcrBatchMainContainer = styled.div<MainContainerProps>`
    display: flex;
    flex: 1;
    flex-direction: ${({$row}) => $row ? 'row' : 'column'};
    min-height: 0;
    overflow: auto;
    
    border-radius: ${tokens.radius['4xl']};
    border: 1px solid ${({theme}) => theme.palette.dividers.subtle};
    background: ${({theme}) => theme.palette.background.paper};
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.02);
`;

export const OcrBatchHeader = styled.div`
    min-height: 65px;
    padding: 8px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid ${({theme}) => theme.palette.dividers.subtle};
    font-size: ${tokens.fontSize.sm};
    font-weight: ${tokens.fontWeight.medium};
    text-transform: uppercase;
    letter-spacing: 0.6px;
    gap: 12px;
`;

export const OcrBatchBox = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

export const OcrBatchCounterBadge = styled.span`
    font-size: ${({theme}) => theme.typography.caption};
    font-weight: ${tokens.fontWeight.medium};
    color: ${({theme}) => theme.palette.text.hint};
    background: ${({theme}) => theme.palette.background.subtle};
    border-radius: ${tokens.radius.xl};
    padding: 2px 10px;
`;

export const OcrBatchLeftPanel = styled.div`
    display: flex;
    flex: 0 0 50%;
    min-width: 0;
    flex-direction: column;
    background: ${({theme}) => theme.palette.background.paper};
    border-right: 1px solid ${({theme}) => theme.palette.dividers.subtle};
`;

export const OcrBatchRightPanel = styled.div`
    display: flex;
    flex: 0 0 50%;
    min-width: 0;
    flex-direction: column;
    background: ${({theme}) => theme.palette.background.paper};
`;

export const OcrBatchContentArea = styled.div`
    flex: 1;
    padding: 24px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: center;
    min-height: 0;
`;

export const OcrBatchPrimaryButton = styled(Button)`
    && {
        display: flex;
        align-items: center;
        gap: 6px;
    }
`;

export const OcrBatchSecondaryButton = styled(Button)`
    && {
        display: flex;
        align-items: center;
        gap: 6px;
    }
`;

export const OcrBatchFileList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
`;

interface FileListItemProps {
    $active?: boolean;
    $clickable?: boolean;
}

export const OcrBatchFileListItem = styled.div<FileListItemProps>`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    
    border-radius: ${tokens.radius.md};
    
    border: 1px solid ${({theme}) => theme.palette.dividers.subtle};
    
    background: ${({theme}) => theme.palette.background.paper};
    
    cursor: ${({$clickable}) => $clickable ? 'pointer' : 'default'};
    transition: background 0.15s, border-color 0.15s;

    &:hover {
        background: ${({$clickable, theme}) => 
                $clickable ? theme.palette.background.dropzone : theme.palette.background.paper};
    }
`;

export const OcrBatchFileInfo = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;

    .name {
        font-size: ${tokens.fontSize.sm};
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        text-transform: none;
    }

    .size {
        font-size: ${tokens.fontSize.xs};
        color: ${({theme}) => theme.palette.text.disabled};
    }
`;

export const OcrBatchFileTypeBadge = styled.span`
    width: fit-content;
    font-size: ${tokens.fontSize.xs};
    font-weight: ${tokens.fontWeight.bold};
    color: ${({theme}) => theme.palette.text.hint};
    background: ${({theme}) => theme.palette.action.selected};
    border-radius: ${tokens.radius.xs};
    padding: 2px 6px;
    flex-shrink: 0;
    align-self: center;
`;

interface OcrFileStatusLabelProps {
    $status: 'PENDING' | 'PROCESSING' | 'SUCCESS' | 'ERROR';
}

const statusColors: Record<string, string> = {
    PENDING: '#94A3B8',
    PROCESSING: '#F59E0B',
    SUCCESS: '#10B981',
    ERROR: '#EF4444',
};

export const OcrBatchStatusLabel = styled.span<OcrFileStatusLabelProps>`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: ${tokens.fontSize.xs};
    font-weight: ${tokens.fontWeight.semibold};
    text-transform: uppercase;
    color: ${({$status}) => statusColors[$status]};
`;

export const OcrBatchProcessingSpinner = styled.div`
    width: 12px;
    height: 12px;
    border: 2px solid ${({theme}) => theme.palette.divider};
    border-top-color: #F59E0B;
    border-radius: 50%;
    flex-shrink: 0;
    animation: spin 0.8s linear infinite;

    @keyframes spin {
        to { transform: rotate(360deg); }
    }
`;

export const OcrBatchToolbar = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 6px;
    padding: 8px 24px;
    border-bottom: 1px solid ${({theme}) => theme.palette.dividers.subtle};
    background: ${({theme}) => theme.palette.background.element};
    flex-shrink: 0;
    min-height: 40px;
`;

export const OcrBatchToolbarLabel = styled.span`
    font-size: ${tokens.fontSize.xs};
    color: ${({theme}) => theme.palette.text.hint};
    cursor: pointer;
    user-select: none;
`;

export const OcrBatchSelectedCount = styled.span`
    margin-left: auto;
    font-size: ${tokens.fontSize.xs};
    font-weight: ${tokens.fontWeight.semibold};
    color: ${({theme}) => theme.palette.text.secondary};
    background: ${({theme}) => theme.palette.action.selected};
    border-radius: ${tokens.radius.xl};
    padding: 2px 10px;
    letter-spacing: ${tokens.letterSpacing.base};
`;